/**
 * Product Coverage Card — home-dashboard summary of the Product Map's
 * capability→flow coverage, modeled on `AppScoreCard`'s look and its
 * `last30Days()` gap-filling approach for the history chart.
 *
 * Headline is the percentage of capabilities with at least one active flow
 * link; `unmappedWorkflowCount` renders beside it unconditionally (honesty
 * rule — a coverage figure never appears without also surfacing what it
 * doesn't yet account for). Per-application rows and the 30-day trend line
 * come straight from `GET /product-map/coverage`, no client-side derivation
 * of anything the server didn't already compute.
 *
 * @module ui/pages/components/ProductCoverageCard
 */

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  LineChart,
  LoadingOrbit,
  Text,
  chartThemes,
  cn,
  useTheme,
} from '@chatsdet/ui';
import { TreeStructure } from '@phosphor-icons/react';
import { useProductCoverage } from '../../../features/product-map/useProductMap';

function formatDayLabel(dateKey: string) {
  const d = new Date(`${dateKey}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function last30Days() {
  const days: { dateKey: string; label: string }[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateKey = d.toISOString().split('T')[0];
    days.push({ dateKey, label: formatDayLabel(dateKey) });
  }
  return days;
}

export function ProductCoverageCard({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { config } = useTheme();
  const chartColors = chartThemes[config.theme];
  const coverageQuery = useProductCoverage(30);
  const coverage = coverageQuery.data;

  const series = React.useMemo(() => {
    const byDate = new Map(coverage?.history.map((point) => [point.captureDate, point]) ?? []);
    return last30Days().map(({ dateKey, label }) => {
      const point = byDate.get(dateKey);
      const linkedPct =
        point && point.capabilitiesTotal > 0
          ? Math.round((point.capabilitiesLinked / point.capabilitiesTotal) * 100)
          : null;
      return { date: label, linkedPct, hasData: Boolean(point) };
    });
  }, [coverage]);

  if (coverageQuery.isLoading) {
    return (
      <Card padding="lg" className={className}>
        <CardContent>
          <Flex align="center" justify="center" direction="column" gap="sm" className="py-6">
            <LoadingOrbit size="lg" />
            <Text size="sm" className="text-muted-foreground">
              Loading product coverage...
            </Text>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (coverageQuery.isError || !coverage) {
    return (
      <Card padding="lg" className={className}>
        <CardContent>
          <Flex direction="column" gap="md" align="start">
            <div>
              <Heading level={4}>Unable to load product coverage</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                We couldn&apos;t fetch the latest data. Try again in a moment.
              </Text>
            </div>
            <Button variant="outline" onPress={() => coverageQuery.refetch()}>
              Retry
            </Button>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  const { overall, byProperty } = coverage.current;

  return (
    <Card padding="lg" className={cn(className)}>
      <CardHeader>
        <Flex align="center" gap="sm">
          <TreeStructure size={20} />
          <Heading level={3}>Product Coverage</Heading>
        </Flex>
      </CardHeader>

      <CardContent>
        {overall.capabilitiesTotal === 0 ? (
          <EmptyState
            icon={<TreeStructure size={24} />}
            title="No capabilities mapped yet"
            description="Define your product's capabilities in the Product Map to start tracking test coverage."
            size="sm"
            action={
              <Button variant="primary" onPress={() => navigate({ to: '/product-map' })}>
                Open Product Map
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
              <div className="min-w-0 flex-1">
                <LineChart
                  height={180}
                  data={series}
                  xDataKey="date"
                  lines={[{ dataKey: 'linkedPct', name: 'Covered %', color: chartColors.success }]}
                  showLegend={false}
                  showYAxis={false}
                  connectNulls={true}
                  gridOpacity={1}
                  xAxisInterval={4}
                />
              </div>

              <div className="flex shrink-0 flex-col items-center justify-center gap-1 self-center px-4">
                <Text size="xs" weight="medium" className="text-muted-foreground">
                  Capabilities covered
                </Text>
                <Flex align="baseline" gap="xs">
                  <Text as="span" className="text-3xl font-semibold tabular-nums">
                    {overall.capabilitiesTotal > 0
                      ? Math.round((overall.capabilitiesLinked / overall.capabilitiesTotal) * 100)
                      : 0}
                    %
                  </Text>
                  <Text as="span" size="sm" className="tabular-nums text-muted-foreground">
                    ({overall.capabilitiesLinked}/{overall.capabilitiesTotal})
                  </Text>
                </Flex>
                {/* Honesty rule: unmapped-flow count always rides alongside the
                    headline figure — see module docstring. */}
                <Text size="xs" className="text-muted-foreground">
                  {overall.capabilitiesCovered} passing ·{' '}
                  {overall.unmappedWorkflowCount} flow{overall.unmappedWorkflowCount === 1 ? '' : 's'}{' '}
                  unmapped
                </Text>
              </div>
            </div>

            {byProperty.length > 0 && (
              <div className="flex flex-col gap-1.5 border-t border-border pt-4">
                {byProperty.map((property) => (
                  <Flex key={property.propertyId} align="center" justify="between" gap="sm">
                    <Text size="sm" weight="medium" className="min-w-0 truncate text-foreground">
                      {property.propertyName}
                    </Text>
                    <Text size="xs" className="shrink-0 tabular-nums text-muted-foreground">
                      {property.capabilitiesLinked}/{property.capabilitiesTotal} covered ·{' '}
                      {property.unmappedWorkflowCount} unmapped
                    </Text>
                  </Flex>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
